import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useVibesites,
  buildScopeContext,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { Lightbox } from './Lightbox';
import { useColor4 } from '../hooks/useColor4';

interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

type GalleryTab = 'all' | 'community' | 'apartment';
type PhotoKind = 'community' | 'apartment';

interface PhotoGroup {
  kind: PhotoKind;
  /** Specific subjects — the earliest one mentioned decides the group. */
  keywords: string[];
  /** Broad words, used only when no group's specific keyword matched. */
  generic?: string[];
}

/**
 * Display order for the gallery. Each entry is one cluster of similar photos;
 * the array order is the order clusters appear in (community before
 * apartment, exterior → outdoor → indoor, then kitchen → … → patio).
 */
const PHOTO_GROUPS: PhotoGroup[] = [
  // Property exterior
  { kind: 'community', keywords: ['exterior', 'building', 'facade', 'façade', 'aerial', 'street view', 'architecture', 'architectural'] },
  { kind: 'community', keywords: ['signage', 'sign', 'monument'] },
  { kind: 'community', keywords: ['main entrance', 'entrance', 'entry', 'gate', 'porte cochere', 'front door'] },
  // Outdoor community
  { kind: 'community', keywords: ['pool', 'swimming', 'sundeck', 'sun deck', 'cabana', 'hot tub', 'jacuzzi', 'poolside'] },
  { kind: 'community', keywords: ['grill', 'grilling', 'bbq', 'barbecue', 'outdoor kitchen', 'fire pit', 'firepit', 'picnic', 'outdoor lounge', 'outdoor entertainment'] },
  { kind: 'community', keywords: ['playground', 'tot lot', 'play area', 'play structure'] },
  { kind: 'community', keywords: ['recreation', 'game lawn', 'bocce', 'cornhole', 'putting green'] },
  { kind: 'community', keywords: ['court', 'tennis', 'basketball', 'pickleball', 'volleyball', 'sport'] },
  { kind: 'community', keywords: ['dog park', 'bark park', 'dog run', 'pet park', 'pet area'] },
  {
    kind: 'community',
    keywords: ['courtyard', 'rooftop', 'roof deck', 'garden', 'trail', 'walking path', 'lake', 'pond', 'green space', 'landscape', 'landscaping', 'landscaped'],
    generic: ['outdoor', 'outside'],
  },
  // Indoor community
  { kind: 'community', keywords: ['clubhouse', 'club house', 'clubroom', 'club room', 'resident lounge', 'community room'] },
  { kind: 'community', keywords: ['lobby', 'leasing', 'reception', 'concierge'] },
  { kind: 'community', keywords: ['business center', 'business centre', 'coworking', 'co-working', 'conference', 'work space', 'workspace', 'study'] },
  { kind: 'community', keywords: ['theater', 'theatre', 'media room', 'cinema', 'screening'] },
  { kind: 'community', keywords: ['fitness', 'gym', 'workout', 'yoga', 'weights', 'cardio'] },
  { kind: 'community', keywords: ['spa', 'sauna', 'massage', 'steam room'] },
  { kind: 'community', keywords: ['package', 'parcel', 'locker', 'mail room', 'mailroom'] },
  { kind: 'community', keywords: ['pet wash', 'pet spa', 'dog wash', 'grooming'] },
  {
    kind: 'community',
    keywords: ['game room', 'billiard', 'billiards', 'elevator', 'hallway', 'corridor'],
    generic: ['lounge', 'amenity', 'amenities', 'indoor'],
  },
  // Apartment interiors
  { kind: 'apartment', keywords: ['kitchen', 'appliance', 'cabinet', 'countertop', 'backsplash', 'pantry', 'refrigerator', 'oven'] },
  { kind: 'apartment', keywords: ['island', 'peninsula', 'breakfast bar'] },
  { kind: 'apartment', keywords: ['living room', 'living area', 'living space', 'living', 'family room', 'great room', 'den'] },
  { kind: 'apartment', keywords: ['dining'] },
  { kind: 'apartment', keywords: ['bedroom', 'primary suite', 'master suite', 'guest room'] },
  { kind: 'apartment', keywords: ['bathroom', 'bath', 'vanity', 'shower', 'tub', 'restroom'] },
  { kind: 'apartment', keywords: ['closet', 'wardrobe'] },
  { kind: 'apartment', keywords: ['washer', 'dryer', 'laundry'] },
  { kind: 'apartment', keywords: ['patio', 'balcony', 'terrace', 'lanai'] },
  {
    kind: 'apartment',
    keywords: ['entryway', 'foyer', 'home office', 'flooring', 'window', 'ceiling'],
    generic: ['interior', 'unit', 'home', 'residence'],
  },
];

const OTHER_COMMUNITY_GROUP = PHOTO_GROUPS.findIndex((g) => g.keywords.includes('game room'));
const OTHER_APARTMENT_GROUP = PHOTO_GROUPS.length - 1;

/** Shots that are fine to keep but should never lead their cluster. */
const WEAK_KEYWORDS = ['toilet', 'empty', 'bare', 'under construction', 'utility', 'water heater', 'storage'];

function imageUrl(photo: GalleryImage): string {
  return photo.image || photo.original_url || '';
}

/**
 * Comparable form of an image URL: protocol, `www.`, hash and trailing slash are
 * ignored; the query string is dropped only when the path already names an image
 * file (resize/cache params), since some hosts identify the image by query.
 */
function urlKey(raw: string | undefined): string {
  const url = (raw || '').trim();
  if (!url) return '';
  try {
    const u = new URL(url, 'https://x.invalid');
    const host = u.host.toLowerCase().replace(/^www\./, '');
    const path = decodeURIComponent(u.pathname).replace(/\/+$/, '');
    const isImageFile = /\.(jpe?g|png|webp|avif|gif|heic)$/i.test(path);
    return `${host}${path}${isImageFile ? '' : u.search}`;
  } catch {
    return url.toLowerCase();
  }
}

/** Drops repeat photos (same image or original URL), keeping the first occurrence. */
function dedupeImages(images: GalleryImage[]): GalleryImage[] {
  const seen = new Set<string>();
  return images.filter((img) => {
    const keys = [urlKey(img.image), urlKey(img.original_url)].filter(Boolean);
    if (keys.length === 0 || keys.some((k) => seen.has(k))) return false;
    keys.forEach((k) => seen.add(k));
    return true;
  });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Earliest whole-word position (plurals allowed) of any keyword in `text`, or -1. */
function firstMatch(text: string, keywords: string[]): number {
  let best = -1;
  for (const kw of keywords) {
    const m = new RegExp(`\\b${escapeRegExp(kw)}(?:s|es)?\\b`, 'i').exec(text);
    if (m && (best === -1 || m.index < best)) best = m.index;
  }
  return best;
}

/** Explicit kind from the photo title (feeds label photos "Apartment" / "Community"). */
function titleKind(photo: GalleryImage): PhotoKind | null {
  const title = (photo.title || '').toLowerCase();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return null;
}

interface ClassifiedPhoto {
  photo: GalleryImage;
  kind: PhotoKind | null;
  group: number;
  staged: boolean;
  weak: boolean;
  sourceIndex: number;
}

function classifyPhoto(photo: GalleryImage, sourceIndex: number): ClassifiedPhoto {
  const fields = [photo.title, photo.alt_text, photo.description].map((f) => (f || '').toLowerCase());
  const allText = fields.join(' ');
  const explicitKind = titleKind(photo);

  // Title is the most deliberate label, then alt text, then description; within a
  // field the keyword that appears first wins ("kitchen with island" → kitchen).
  const findGroup = (pick: (g: PhotoGroup) => string[] | undefined): number => {
    for (const text of fields) {
      if (!text) continue;
      let found = -1;
      let bestPos = -1;
      PHOTO_GROUPS.forEach((g, i) => {
        const kws = pick(g);
        if (!kws || (explicitKind && g.kind !== explicitKind)) return;
        const pos = firstMatch(text, kws);
        if (pos !== -1 && (bestPos === -1 || pos < bestPos)) {
          bestPos = pos;
          found = i;
        }
      });
      if (found !== -1) return found;
    }
    return -1;
  };

  let group = findGroup((g) => g.keywords);
  if (group === -1) group = findGroup((g) => g.generic);

  const kind: PhotoKind | null = explicitKind ?? (group !== -1 ? PHOTO_GROUPS[group].kind : null);
  if (group === -1 && kind) {
    group = kind === 'community' ? OTHER_COMMUNITY_GROUP : OTHER_APARTMENT_GROUP;
  }

  const staged = /\bstaged\b/.test(allText) && !/\b(un-?staged|not staged)\b/.test(allText);
  const weak = firstMatch(allText, WEAK_KEYWORDS) !== -1;

  return { photo, kind, group, staged, weak, sourceIndex };
}

/**
 * Category order first, then strong shots before weak ones, staged before
 * unstaged, and otherwise the feed's own order so the same space stays together.
 */
function compareClassified(a: ClassifiedPhoto, b: ClassifiedPhoto): number {
  const ga = a.group === -1 ? Number.MAX_SAFE_INTEGER : a.group;
  const gb = b.group === -1 ? Number.MAX_SAFE_INTEGER : b.group;
  if (ga !== gb) return ga - gb;
  if (a.weak !== b.weak) return a.weak ? 1 : -1;
  if (a.staged !== b.staged) return a.staged ? -1 : 1;
  return a.sourceIndex - b.sourceIndex;
}

const TABS: { key: GalleryTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'community', label: 'Community' },
  { key: 'apartment', label: 'Apartment' },
];

export function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedTab, setActiveTab] = useState<GalleryTab>('all');
  const color4 = useColor4();

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  // A manual order saved in the editor wins over the automatic category sort.
  const { collectionBindingOverrides, page } = useVibesites();
  const override = resolveScopedOverride(
    collectionBindingOverrides,
    'gallery.images',
    buildScopeContext(page),
  );
  const hasManualOrder = Boolean(override?.orderedIds?.length || override?.ordering?.length);

  const classified = useMemo(() => {
    const list = dedupeImages((images ?? []).filter((img) => imageUrl(img)))
      .map((img, i) => classifyPhoto(img, i));
    return hasManualOrder ? list : [...list].sort(compareClassified);
  }, [images, hasManualOrder]);

  const tabCounts = useMemo(() => {
    let apt = 0;
    let com = 0;
    for (const c of classified) {
      if (c.kind === 'apartment') apt++;
      else if (c.kind === 'community') com++;
    }
    return { all: classified.length, apartment: apt, community: com };
  }, [classified]);

  const activeTab: GalleryTab = tabCounts[selectedTab] > 0 ? selectedTab : 'all';

  const filteredImages = useMemo(() => {
    const list = activeTab === 'all' ? classified : classified.filter((c) => c.kind === activeTab);
    return list.map((c) => c.photo);
  }, [classified, activeTab]);

  const lightboxImages = useMemo(
    () => filteredImages.map((img) => ({ url: imageUrl(img), alt: img.alt_text || img.title || '' })),
    [filteredImages],
  );

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tab filters */}
        {classified.length > 0 && (
          <div className="gallery-tabs">
            {TABS.map((tab) => {
              const count = tabCounts[tab.key];
              if (tab.key !== 'all' && count === 0) return null;
              return (
                <button
                  key={tab.key}
                  className={`gallery-tab${activeTab === tab.key ? ' gallery-tab--active' : ''}`}
                  style={activeTab === tab.key ? color4?.primaryOverride : undefined}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSelectedIndex(null);
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Photo grid */}
        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images">
          {filteredImages.length > 0 ? (
            <div className="gallery-grid grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {filteredImages.map((img, index) => (
                <div
                  key={`${activeTab}-${index}`}
                  className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                  onClick={() => setSelectedIndex(index)}
                >
                  <VibesitesImage
                    url={imageUrl(img)}
                    alt={img.alt_text || img.title || ''}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    sizes="(min-width: 1024px) 264px, (min-width: 768px) 33vw, 50vw"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Gallery images will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onSelect={setSelectedIndex}
      />

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: var(--vibe-font-weight-medium);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .gallery-tab:first-child {
          border-radius: 3px 0 0 3px;
        }
        .gallery-tab:last-child {
          border-radius: 0 3px 3px 0;
        }
        .gallery-tab:hover {
          color: var(--vibe-color-text);
          border-color: var(--vibe-color-text-muted);
        }
        .gallery-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .gallery-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }

        .gallery-grid {
          gap: 0.75rem;
        }

        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as any).contentSchema = contentSchema;

export default Gallery;
